/**
 * Zone Configuration - The 8 creative disciplines of Red Social
 * Each zone represents a different creative domain where projects live
 *
 * ─────────────────────────────────────────────────────────────────────────
 * COLOR SOURCE OF TRUTH
 * ─────────────────────────────────────────────────────────────────────────
 * Every color here derives from src/config/tokens.js. This file declares NO
 * hex values of its own — previously it held the eight zone hexes a second
 * time, plus the same colors encoded a THIRD way as Tailwind palette strings
 * ('bg-amber-500/20'). Changing a zone in tokens.js updated the hex and left
 * those class strings silently on the old color.
 *
 * The Tailwind classes below use the `zone-*` / `type-*` / `unzoned`
 * utilities generated by tailwind.config.js from those same tokens. They are
 * written as complete literal strings so Tailwind's scanner can see them —
 * that is what allows the safelist to be removed from the config. If you ever
 * build one of these at runtime (`bg-zone-${id}`), the scanner cannot see it
 * and it WILL be purged from the production build while working fine in dev.
 * Add it back to the safelist in that case.
 *
 * NOTE ON TEXT COLORS: these were `text-amber-400`, `text-pink-400` etc.
 * (Tailwind's 400 step); they are now `text-zone-*`, which maps to the 500-step
 * token. Zone label text renders very slightly darker. This is deliberate —
 * exactness against the token beats matching the old shade. Use
 * `text-zone-build/90` if you want to approximate the previous lightness.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CHANGELOG — August 2026 classification pass
 * ─────────────────────────────────────────────────────────────────────────
 * Triggered by project 62 "Taste Evolution" (a wine-tasting post) rendering
 * in Build Something. Diagnosis: it scored ZERO in all eight zones, so
 * execution fell through to the type fallback (`physical` -> Build). Five
 * changes, each independently defensible:
 *
 *   1. CATEGORY PIN. If `category` exactly names a zone (id, short label or
 *      full label), that zone wins outright with no scoring. Zero-migration
 *      manual override — see resolvePinnedZone().
 *   2. TASTE VOCABULARY. The Taste keyword list contained neither "taste"
 *      nor "wine". Filled that gap and reconciled the list against
 *      backend/zone_config.py, which had 'flavor'/'dish'/'seasoning' the
 *      frontend never received.
 *   3. DICTIONARY OVERRIDES. WordNet files a lot of kitchen vocabulary under
 *      the wrong lexname: cooking/baking/kitchen/cake -> build,
 *      recipe/menu -> story, taste/flavor -> smart. DICTIONARY_OVERRIDES
 *      corrects those and mutes a few generic words.
 *   4. CURATED WORDS OUTRANK WORDNET. Any tag word that already appears in a
 *      curated keyword list is excluded from the dictionary pass entirely.
 *      Previously a tag of "cooking" scored Taste 10 (curated) AND Build 8
 *      (WordNet) — three such tags and Build won using Taste's own words.
 *   5. DETERMINISTIC TIE-BREAKS. `Object.values(ZONES)` is ordered
 *      build, beautiful, sound, taste... and Array.sort() is stable, so every
 *      score tie silently resolved to Build first and Beautiful second. Ties
 *      now break on evidence quality (tag > file > category > name >
 *      description) before falling back to declaration order.
 *
 * Also: the dictionary pass used to run inside scoreZone(), i.e. the tags
 * were re-tokenized and re-scanned once per zone, eight times per project,
 * on every layout recompute. It is now computed once in scoreAllZones().
 *
 * New export: explainProjectZone(project) returns the full score breakdown.
 * Use it before editing anything in this file.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { lookupWordZone } from './zones_dictionary.js';
import zonesConfig from '../../../backend/zones.config.json';
import { TYPE_COLORS, UNZONED_COLOR } from './tokens.js';

/**
 * Zone hexes, keyed by id — derived, not declared.
 *
 * This used to live in tokens.js. It moved here because tokens.js is loaded
 * by Node when Tailwind reads its config, and a bare JSON import throws
 * ERR_IMPORT_ATTRIBUTE_MISSING under plain Node. tokens.js is back to zero
 * imports (as its own header demands) and tailwind.config.js reads the JSON
 * with fs instead. Nothing in the Node path imports JSON any more.
 */
export const ZONE_COLORS = Object.fromEntries(
  Object.entries(zonesConfig.zones).map(([id, z]) => [id, z.color]),
);

/**
 * '#f59e0b' -> [245, 158, 11]
 *
 * The `rgb` field on each zone used to be a hand-maintained array sitting
 * next to the hex — two encodings of one value, with nothing checking they
 * agreed. Deriving it means they cannot drift.
 *
 * (As of this writing nothing in the app reads `.rgb`. It is kept because it
 * costs nothing and removing a public field is a bigger change than this
 * refactor warrants.)
 */
const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// ==========================================================================
// ZONES — GENERATED FROM backend/zones.config.json
// ==========================================================================
// This used to be ~180 lines of hand-maintained literals: label, shortLabel,
// color, four Tailwind class strings, icon, keywords and description, eight
// times over — with a near-duplicate of the same data in zone_config.py and a
// third copy of the ids in tailwind.config.js.
//
// Now: one object per zone in backend/zones.config.json. Same runtime shape,
// so every `zone.textColor` / `zone.keywords` / `ZONES.taste` call site in the
// app is untouched.
//
// The `rgb` field is derived rather than stored, as before.
// ==========================================================================

export const ZONES = Object.fromEntries(
  Object.entries(zonesConfig.zones).map(([id, z]) => [id, {
    id,
    label: z.label,
    shortLabel: z.shortLabel,
    color: z.color,
    rgb: hexToRgb(z.color),
    gradient: z.classes.gradient,
    bgGlow: z.classes.bgGlow,
    borderColor: z.classes.borderColor,
    textColor: z.classes.textColor,
    icon: z.icon,
    keywords: z.keywords,
    description: z.description,
    // Suggested tags shown as chips in CreateProjectOverlay.
    suggestedTags: z.suggestedTags || [],
  }]),
);

/**
 * Zone IDs are frozen (see the _doc block in zones.config.json). If one ever
 * genuinely has to change, add the old value here instead of doing a
 * migration: old ids arriving from the database, the WordNet dictionary or a
 * partner API payload keep resolving.
 *
 *     const ZONE_ID_ALIASES = { health: 'heal' };
 */
export const ZONE_ID_ALIASES = {};

/** Resolve a possibly-aliased zone id to a live zone, or null. */
export function resolveZoneId(id) {
  if (!id) return null;
  const key = String(id).trim().toLowerCase();
  return ZONES[key] || ZONES[ZONE_ID_ALIASES[key]] || null;
}

// Uncategorized fallback — uses the `unzoned` utility generated from
// UNZONED_COLOR in tokens.js (same value the map uses for zoneless nodes).
export const UNCATEGORIZED_ZONE = {
  id: 'uncategorized',
  label: 'Explore',
  shortLabel: 'Explore',
  color: UNZONED_COLOR,
  rgb: hexToRgb(UNZONED_COLOR),
  gradient: 'from-unzoned to-unzoned/60',
  bgGlow: 'bg-unzoned/20',
  borderColor: 'border-unzoned/40',
  textColor: 'text-unzoned',
  icon: 'Compass',
  keywords: [],
  description: 'Uncategorized projects'
};

// Project type configuration — ring colors on the cosmic map.
// Colors come from TYPE_COLORS in tokens.js; classes use the generated
// `type-*` utilities.
export const PROJECT_TYPES = {
  physical: {
    id: 'physical',
    label: 'Physical',
    color: TYPE_COLORS.physical,
    bgColor: 'bg-type-physical/20',
    textColor: 'text-type-physical',
    borderColor: 'border-type-physical/40',
    icon: 'Package'
  },
  digital: {
    id: 'digital',
    label: 'Digital',
    color: TYPE_COLORS.digital,
    bgColor: 'bg-type-digital/20',
    textColor: 'text-type-digital',
    borderColor: 'border-type-digital/40',
    icon: 'Monitor'
  },
  hybrid: {
    id: 'hybrid',
    label: 'Hybrid',
    color: TYPE_COLORS.hybrid,
    bgColor: 'bg-type-hybrid/20',
    textColor: 'text-type-hybrid',
    borderColor: 'border-type-hybrid/40',
    icon: 'Layers'
  }
};

// Project status configuration
// NOTE: `color` here is a bare Tailwind palette NAME (not a token), consumed
// by call sites that build their own class strings. Left as-is — these are UI
// state colors, not zone identity, so they do not belong in tokens.js.
export const PROJECT_STATUSES = {
  idea: { label: 'Idea', color: 'purple', icon: 'Sparkles', progress: 10 },
  planning: { label: 'Planning', color: 'blue', icon: 'Target', progress: 25 },
  in_progress: { label: 'In Progress', color: 'amber', icon: 'Zap', progress: 50 },
  seeking_contributors: { label: 'Seeking Help', color: 'emerald', icon: 'Users', progress: 40 },
  review: { label: 'In Review', color: 'cyan', icon: 'Eye', progress: 75 },
  completed: { label: 'Completed', color: 'green', icon: 'CheckCircle2', progress: 100 },
  archived: { label: 'Archived', color: 'zinc', icon: 'Archive', progress: 100 }
};

// ==========================================================================
// FILE-TYPE → ZONE HINTS
// ==========================================================================
// When a project has uploaded files, the file extensions provide a secondary
// signal about which zone it belongs in.  This is the 5-point tier — stronger
// than description text (1 pt) but weaker than explicit tags (10 pts).
//
// null = no zone signal (images, generic docs).
// ==========================================================================

export const FILE_TYPE_ZONE_HINTS = {
  // CAD / Build
  dwg: 'build', dxf: 'build', step: 'build', stp: 'build', iges: 'build', igs: 'build',
  stl: 'build', sldprt: 'build', sldasm: 'build', rvt: 'build', skp: 'build',
  // 3D modeling — leans Build (physical prototyping) unless tags say otherwise
  blend: 'build', fbx: 'build', obj: 'build', gltf: 'build', glb: 'build',
  '3ds': 'build', dae: 'build',
  // Design / Beautiful
  psd: 'beautiful', ai: 'beautiful', xd: 'beautiful', sketch: 'beautiful',
  fig: 'beautiful', indd: 'beautiful', afdesign: 'beautiful', afphoto: 'beautiful',
  kra: 'beautiful', xcf: 'beautiful', cdr: 'beautiful', eps: 'beautiful',
  svg: 'beautiful',
  // Audio / Sound
  mp3: 'sound', wav: 'sound', flac: 'sound', aac: 'sound', ogg: 'sound',
  aiff: 'sound', m4a: 'sound', wma: 'sound',
  // Video / Story
  mp4: 'story', mov: 'story', avi: 'story', mkv: 'story', webm: 'story',
  wmv: 'story', flv: 'story', m4v: 'story',
  // Code / Smart
  py: 'smart', js: 'smart', jsx: 'smart', ts: 'smart', tsx: 'smart',
  java: 'smart', c: 'smart', cpp: 'smart', h: 'smart', hpp: 'smart',
  cs: 'smart', go: 'smart', rs: 'smart', rb: 'smart', php: 'smart',
  swift: 'smart', kt: 'smart', scala: 'smart', r: 'smart',
  sql: 'smart', sh: 'smart', bash: 'smart', ps1: 'smart',
  ino: 'smart',  // Arduino
  // GIS & Geospatial → Smart
  qgz: 'smart', qgs: 'smart', gpkg: 'smart', shp: 'smart', shx: 'smart',
  dbf: 'smart', prj: 'smart', geojson: 'smart', kml: 'smart', kmz: 'smart',
  gdb: 'smart', mxd: 'smart', aprx: 'smart', lyrx: 'smart', gpx: 'smart',
  osm: 'smart', asc: 'smart', las: 'smart', laz: 'smart',
  // Motion capture
  bvh: 'smart',
  // No strong zone signal
  tif: null, pdf: null, doc: null, docx: null, txt: null, rtf: null, odt: null, md: null,
  jpg: null, jpeg: null, png: null, gif: null, webp: null, bmp: null, tiff: null,
  heic: null, heif: null, ico: null,
  xls: null, xlsx: null, csv: null, ppt: null, pptx: null,
  zip: null, rar: null, '7z': null, tar: null, gz: null,
  json: null, xml: null, yaml: null, yml: null,
};

/**
 * Extract file extensions from a project's uploaded items.
 * Works with both digital_items array and the raw files list.
 * Returns lowercase extensions without dots, e.g. ['dwg', 'step', 'pdf'].
 */
export function extractFileExtensions(project) {
  const exts = new Set();

  // digital_items from API
  const items = project.digital_items || project.digitalItems || [];
  for (const item of items) {
    const name = item.file_url || item.fileUrl || item.filename || item.name || '';
    const dot = name.lastIndexOf('.');
    if (dot !== -1) exts.add(name.slice(dot + 1).toLowerCase());
  }

  // physical_items sometimes have attached images/docs
  const physItems = project.physical_items || project.physicalItems || [];
  for (const item of physItems) {
    const name = item.image_url || item.imageUrl || '';
    const dot = name.lastIndexOf('.');
    if (dot !== -1) exts.add(name.slice(dot + 1).toLowerCase());
  }

  return [...exts];
}


// ==========================================================================
// CATEGORY PIN — MANUAL OVERRIDE, NO MIGRATION
// ==========================================================================
// `projects.category` is a nullable free-text column that nothing on the map
// depends on. If its value exactly names a zone, we treat that as an explicit
// human decision and skip scoring entirely.
//
// Accepted forms (case-insensitive, whitespace-trimmed):
//     'taste'                 → zone id
//     'Taste'                 → short label
//     'Make It Taste Good'    → full label
//
// Anything else (e.g. 'Art', 'Culinary Arts', NULL) does not pin, and the
// project falls through to normal scoring exactly as before.
//
// A future `zone_override` / `zoneOverride` field is also honored, so adding
// that column later needs no code change here.
//
// WHY THIS EXISTS: a demo post that lands in the wrong zone is a credibility
// problem you cannot fix by arguing with a scorer. One UPDATE and it is done.
// ==========================================================================

const _pinLookup = (() => {
  const m = new Map();
  for (const z of Object.values(ZONES)) {
    m.set(z.id.toLowerCase(), z);
    m.set(z.shortLabel.toLowerCase(), z);
    m.set(z.label.toLowerCase(), z);
  }
  return m;
})();

/**
 * Returns the pinned zone for a project, or null if it is not pinned.
 * Deliberately does NOT read `project.zone` — the backend's own
 * detect_project_zone() result is not authoritative for map placement, and
 * quietly making it so would move a lot of nodes without anyone deciding to.
 */
export function resolvePinnedZone(project) {
  if (!project) return null;
  const raw = project.zone_override ?? project.zoneOverride ?? project.category ?? '';
  const key = String(raw ?? '').trim().toLowerCase();
  if (!key) return null;
  return _pinLookup.get(key) || resolveZoneId(key) || null;
}


// ==========================================================================
// WORD BOUNDARY HELPER
// ==========================================================================
// Matches whole words only — "art" matches "art" and "art direction" but
// NOT "startup", "smart", or "heart". Uses \b word-boundary regex anchors.
// Multi-word keywords like "sound design" match as a contiguous phrase.

const _wordBoundaryCache = new Map();

function matchesWholeWord(text, keyword) {
  let re = _wordBoundaryCache.get(keyword);
  if (!re) {
    // Escape regex special chars in keyword, then wrap with \b
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    re = new RegExp(`\\b${escaped}\\b`, 'i');
    _wordBoundaryCache.set(keyword, re);
  }
  return re.test(text);
}

// ==========================================================================
// CURATED WORD INDEX
// ==========================================================================
// Every individual word that appears anywhere in any zone's curated keyword
// list, phrases included ("sound design" contributes 'sound' and 'design').
//
// Used to keep WordNet out of territory you have already mapped by hand.
// See scoreTagsWithDictionary() below.

const CURATED_WORDS = (() => {
  const s = new Set();
  for (const zone of Object.values(ZONES)) {
    for (const kw of zone.keywords) {
      for (const w of kw.toLowerCase().split(/\s+/)) {
        if (w) s.add(w);
      }
    }
  }
  return s;
})();

// ==========================================================================
// DICTIONARY OVERRIDES
// ==========================================================================
// zones_dictionary.js is a WordNet lexname export. WordNet's categories were
// built for linguistics, not for this product, and it puts a lot of everyday
// making-and-eating vocabulary in surprising places:
//
//     taste -> smart    (filed under cognition)
//     flavor -> smart   (also cognition)
//     cooking -> build  (an "act of creation")
//     baking -> build   kitchen -> build   cake -> build   plate -> build
//     recipe -> story   menu -> story      (both filed as "communication")
//
// Words listed here take precedence over the dictionary.
//   'zoneid' → force that zone
//   null     → mute the word entirely (too generic to carry a zone signal)
//
// Only add a word here when WordNet is actively WRONG for this product. If a
// word simply is not in the dictionary, add it to a zone's keywords instead —
// that is the curated tier and it scores higher.
// ==========================================================================

export const DICTIONARY_OVERRIDES = {
  // Kitchen vocabulary WordNet files under Build
  cooking: 'taste', cook: 'taste', baking: 'taste', bake: 'taste',
  kitchen: 'taste', cake: 'taste', pie: 'taste', dough: 'taste',
  grill: 'taste', roast: 'taste', simmer: 'taste', braise: 'taste',
  // Filed under Story ("communication")
  recipe: 'taste', menu: 'taste', cookbook: 'taste',
  // Filed under Smart ("cognition")
  taste: 'taste', tasting: 'taste', flavor: 'taste', flavour: 'taste',
  aroma: 'taste', gustation: 'taste', palate: 'taste',
  // Genuinely ambiguous — a plate can be steel or dinnerware, a dish can be
  // satellite or supper. Muted rather than guessed.
  plate: null, dish: null, press: null, mold: null, mould: null,
  // Generic words that carry no discipline signal in a tag
  evolution: null, project: null, final: null, draft: null, series: null,
  study: null, experiment: null, dry: null, wet: null, light: null,
  self: null, work: null, thing: null, room: null, line: null, place: null,
};

// Points awarded per dictionary-matched tag word. A curated exact tag match
// is worth 10; keeping this at 8 preserves the pre-August behavior for every
// word the curated lists do not already claim.
const DICTIONARY_WEIGHT = 8;

/**
 * Score a project's tags through the WordNet dictionary, once.
 *
 * TAGS ONLY, deliberately — unchanged policy, restated because it keeps
 * getting re-litigated:
 *
 * WHY TAGS: a student typing "wine", "terroir", "tannin" or "gustation" has
 * chosen that word. The dictionary is what maps those to Taste when the
 * curated list does not. That is the wine-class bug this dictionary was built
 * to fix (May 2026) — do not remove this pass.
 *
 * WHY NOT NAME/DESCRIPTION: there used to be a second pass scoring the name
 * and description at 1 point per matched word. The dictionary is a 40,798-
 * word WordNet export and contains most ordinary English. Any description
 * written in plain English scored several zones on incidental words, at the
 * same weight as a curated keyword. A project called "self portrait 1",
 * described as "I took a self portrait" and marked PHYSICAL, landed in Make
 * It Smart — decided by the pronoun "self".
 *
 * Tags are intent. Prose is not. Only tags vote.
 *
 * Two filters applied here (August 2026):
 *   1. Words in CURATED_WORDS are skipped. You already decided where those
 *      belong, at 10 points; WordNet does not get a second, contradictory
 *      vote on the same word.
 *   2. DICTIONARY_OVERRIDES wins over the dictionary.
 *
 * @returns {Object<string, number>} zoneId → points
 */
function scoreTagsWithDictionary(projectTags) {
  const scores = {};
  const text = projectTags.join(' ').toLowerCase();
  if (!text) return scores;

  const words = text.split(/[\s,;/|+&.!?()[\]{}'"-]+/).filter(Boolean);

  for (const word of words) {
    // Filter 1 — curated lists outrank WordNet on words they already claim
    if (CURATED_WORDS.has(word)) continue;

    // Filter 2 — explicit overrides beat the dictionary
    let zoneId;
    if (Object.prototype.hasOwnProperty.call(DICTIONARY_OVERRIDES, word)) {
      zoneId = DICTIONARY_OVERRIDES[word];
    } else {
      zoneId = lookupWordZone(word);
    }

    if (zoneId && ZONES[zoneId]) {
      scores[zoneId] = (scores[zoneId] || 0) + DICTIONARY_WEIGHT;
    }
  }

  return scores;
}

// ==========================================================================
// PHRASE SUPPRESSION — PRE-PASS
// ==========================================================================
// Before scoring individual zones, scan ALL zone keywords against the
// project's text fields. When a multi-word keyword matches (e.g. "sound
// design" for Sound), its component words ("sound", "design") are
// "consumed" for that zone. Later, when scoring single-word keywords,
// if that word was already consumed by a DIFFERENT zone's phrase match,
// it is suppressed — preventing "design" alone from scoring for Beautiful
// when it was part of "sound design" → Sound.
//
// This only suppresses cross-zone noise. If "design" matches Beautiful
// via a phrase in Beautiful's own keywords (e.g. "graphic design"), it
// still scores for Beautiful normally.

function buildPhraseSuppressions(projectTags, projectCategory, projectName, projectDesc) {
  // Map: individual word → Set of zone IDs that consumed it via phrase match
  const consumedWords = new Map();
  const allText = [projectTags.join(' '), projectCategory, projectName, projectDesc].join(' ');

  for (const zone of Object.values(ZONES)) {
    // Only look at multi-word keywords (phrases)
    const phrases = zone.keywords.filter(kw => kw.includes(' '));

    for (const phrase of phrases) {
      const phraseLower = phrase.toLowerCase();

      // Check if phrase appears anywhere in project text or tags
      const inTags = projectTags.some(tag => matchesWholeWord(tag, phraseLower) || tag === phraseLower);
      const inText = matchesWholeWord(allText, phraseLower);

      if (inTags || inText) {
        // Consume each individual word in the phrase for this zone
        const words = phraseLower.split(/\s+/);
        for (const word of words) {
          if (!consumedWords.has(word)) consumedWords.set(word, new Set());
          consumedWords.get(word).add(zone.id);
        }
      }
    }
  }

  return consumedWords;
}

// ==========================================================================
// PHRASE BONUS MULTIPLIER
// ==========================================================================
// Multi-word keywords are inherently more specific and less ambiguous.
// "sound design" is a stronger signal than just "sound" or "design".
// 2-word phrases: 1.5x multiplier, 3+ word phrases: 2x multiplier.

function phraseMultiplier(keyword) {
  const wordCount = keyword.split(/\s+/).length;
  if (wordCount >= 3) return 2.0;
  if (wordCount >= 2) return 1.5;
  return 1.0;
}

/**
 * Score how well a project matches a zone using weighted keyword matching.
 *
 * Returns a BREAKDOWN, not a single number, so ties can be resolved on the
 * quality of the evidence rather than on object key order, and so
 * explainProjectZone() has something to show you.
 *
 *   1. WORD BOUNDARIES: "art" matches "art" but not "startup" or "smart".
 *   2. PHRASE BONUS: "sound design" → 1.5x, "supply chain logistics" → 2x.
 *   3. PHRASE SUPPRESSION: a single word consumed by another zone's phrase
 *      match does not also score here.
 *
 * Weighting hierarchy:
 *   - Exact tag match        → 10 pts × phrase multiplier
 *   - Dictionary tag match   →  8 pts (computed once, passed in)
 *   - File type hint         →  5 pts (no multiplier — extensions are single tokens)
 *   - Category match         →  5 pts × phrase multiplier
 *   - Project name match     →  3 pts × phrase multiplier
 *   - Description match      →  1 pt  × phrase multiplier
 */
function scoreZone(zone, projectTags, projectCategory, projectName, projectDesc, fileExtensions, consumedWords, dictScores) {
  let tagPts = 0;
  let categoryPts = 0;
  let namePts = 0;
  let descPts = 0;
  let filePts = 0;
  const matched = [];

  for (const keyword of zone.keywords) {
    const kw = keyword.toLowerCase();
    const isPhrase = kw.includes(' ');
    const multiplier = phraseMultiplier(kw);

    // If this is a single-word keyword, check if it was consumed by a
    // multi-word phrase match in a DIFFERENT zone. If so, suppress it.
    if (!isPhrase && consumedWords.has(kw)) {
      const consumingZones = consumedWords.get(kw);
      if (consumingZones.size > 0 && !consumingZones.has(zone.id)) {
        continue; // Skip — this word is "owned" by another zone's phrase
      }
    }

    // Tag matching — exact match or whole-word containment
    for (const tag of projectTags) {
      if (tag === kw) {
        tagPts += 10 * multiplier;
        matched.push(`tag:${kw}`);
      } else if (isPhrase && matchesWholeWord(tag, kw)) {
        tagPts += 10 * multiplier;
        matched.push(`tag:${kw}`);
      } else if (!isPhrase && tag.split(/[\s,;/|+&-]+/).includes(kw)) {
        // e.g. keyword "art" matches tag "digital art" but NOT tag "startup"
        tagPts += 10 * multiplier;
        matched.push(`tag:${kw}`);
      }
    }

    if (projectCategory && matchesWholeWord(projectCategory, kw)) {
      categoryPts += 5 * multiplier;
      matched.push(`category:${kw}`);
    }

    if (matchesWholeWord(projectName, kw)) {
      namePts += 3 * multiplier;
      matched.push(`name:${kw}`);
    }

    if (matchesWholeWord(projectDesc, kw)) {
      descPts += 1 * multiplier;
      matched.push(`desc:${kw}`);
    }
  }

  // File-type zone hints (5 pts per matching extension)
  for (const ext of fileExtensions) {
    if (FILE_TYPE_ZONE_HINTS[ext] === zone.id) {
      filePts += 5;
      matched.push(`file:.${ext}`);
    }
  }

  const dictPts = dictScores[zone.id] || 0;

  return {
    total: tagPts + dictPts + filePts + categoryPts + namePts + descPts,
    tagPts, dictPts, filePts, categoryPts, namePts, descPts,
    matched,
  };
}

/**
 * Shared helper — extracts and normalizes all project text fields once,
 * builds phrase suppressions once, scores the dictionary once, then scores
 * all zones.
 *
 * Returns an array of { zone, score, breakdown } sorted best-first.
 *
 * TIE-BREAKING (August 2026): previously this was a bare
 * `.sort((a, b) => b.score - a.score)`. Array.sort is stable and
 * Object.values(ZONES) is ordered build, beautiful, sound, taste..., so every
 * tie resolved to Build, then Beautiful — the two zones untagged projects
 * were already landing in. Ties now break on evidence quality:
 * explicit tags first, then files, then category, then name, then
 * description, and only then declaration order.
 */
function scoreAllZones(project) {
  const projectTags = (project.tags || []).map(t =>
    (typeof t === 'string' ? t : t?.name || '').toLowerCase()
  );
  const projectCategory = (project.category || '').toLowerCase();
  const projectName = (project.name || '').toLowerCase();
  const projectDesc = (project.description || '').toLowerCase();
  const fileExtensions = extractFileExtensions(project);

  // Pre-pass — find all phrase matches across all zones
  const consumedWords = buildPhraseSuppressions(projectTags, projectCategory, projectName, projectDesc);

  // Dictionary pass — ONCE, not once per zone (this used to run 8x)
  const dictScores = scoreTagsWithDictionary(projectTags);

  return Object.values(ZONES)
    .map(zone => {
      const breakdown = scoreZone(
        zone, projectTags, projectCategory, projectName, projectDesc,
        fileExtensions, consumedWords, dictScores
      );
      return { zone, score: breakdown.total, breakdown };
    })
    .sort((a, b) =>
      b.score - a.score ||
      b.breakdown.tagPts - a.breakdown.tagPts ||
      b.breakdown.filePts - a.breakdown.filePts ||
      b.breakdown.categoryPts - a.breakdown.categoryPts ||
      b.breakdown.namePts - a.breakdown.namePts ||
      b.breakdown.descPts - a.breakdown.descPts
    );
}

/**
 * Inherited zone context threshold.
 * When a fork's own keyword score is below this value, we consider its
 * signal "weak" and check whether a parent project's zone should carry
 * forward. This prevents forks with lazy/missing tags from bouncing to
 * an unrelated zone (or the generic Smart/Build fallback) when they
 * clearly belong in the same discipline as their parent.
 *
 * 10 pts = one exact tag match. Below that, the project has at best a
 * partial name/description hit — not enough to confidently override the
 * parent lineage.
 */
const INHERITED_ZONE_THRESHOLD = 10;

/**
 * Dampened weight applied to the parent's zone when inheriting.
 * The parent zone gets this many bonus points added to its score in the
 * child's zone ranking. Set high enough to beat the type-based fallback
 * but low enough that a single strong tag on the child (10 pts) still
 * wins. 8 pts means: "if you have nothing better, stay in your parent's
 * zone" without overriding genuine cross-zone signals.
 */
const INHERITED_ZONE_WEIGHT = 8;

/**
 * Type-based last resort. Reached only when a project has NO tags, NO
 * category, NO recognized words in its name or description, NO files with a
 * zone hint, and NO parent.
 *
 * This is a guess, and it is worth knowing how often it fires — at Cabrillo
 * scale, most students will not tag anything, and every untagged physical
 * project silently becomes woodworking. explainProjectZone() reports
 * `usedTypeFallback: true` when this path is taken.
 */
function typeFallbackZone(project) {
  const type = project.project_type || project.projectType || 'digital';
  if (type === 'physical') return ZONES.build;
  if (type === 'digital') return ZONES.smart;
  return ZONES.beautiful;
}

/**
 * Get the primary zone for a project.
 *
 * Resolution order:
 *   0. Category pin — `category` exactly names a zone → that zone, no scoring
 *   1. Weighted keyword + dictionary + file scoring
 *   2. Parent zone inheritance when the child's own signal is weak
 *   3. project_type fallback
 *
 * @param {object}  project         - The project to classify
 * @param {object}  [parentProject] - Optional parent project for inherited context
 */
export function getProjectZone(project, parentProject) {
  if (!project) return UNCATEGORIZED_ZONE;

  // 0. Explicit human decision wins outright.
  const pinned = resolvePinnedZone(project);
  if (pinned) return pinned;

  const scored = scoreAllZones(project);

  if (scored.length > 0 && scored[0].score > 0) {
    // Strong signal — child's own keywords are decisive
    if (scored[0].score >= INHERITED_ZONE_THRESHOLD) {
      return scored[0].zone;
    }

    // Weak signal — check if parent context should carry forward
    if (parentProject) {
      const parentZone = getProjectZone(parentProject); // no grandparent recursion
      const parentEntry = scored.find(s => s.zone.id === parentZone.id);
      if (parentEntry) {
        // Boost the parent's zone and re-sort
        const boosted = scored.map(s =>
          s.zone.id === parentZone.id
            ? { ...s, score: s.score + INHERITED_ZONE_WEIGHT }
            : s
        ).sort((a, b) => b.score - a.score);
        return boosted[0].zone;
      }
      // Parent zone not in scored list at all — inject it
      if (INHERITED_ZONE_WEIGHT > scored[0].score) {
        return parentZone;
      }
    }

    // No parent context — use the child's best score as-is
    return scored[0].zone;
  }

  // No matches at all — try parent context before type fallback
  if (parentProject) {
    return getProjectZone(parentProject);
  }

  return typeFallbackZone(project);
}

/**
 * Get all matching zones for a project (for multi-zone positioning).
 * Returns zones that score above zero, sorted by score.
 */
export function getProjectZones(project) {
  if (!project) return [UNCATEGORIZED_ZONE];

  const pinned = resolvePinnedZone(project);
  if (pinned) return [pinned];

  const scored = scoreAllZones(project).filter(({ score }) => score > 0);
  if (scored.length > 0) return scored.map(s => s.zone);

  return [typeFallbackZone(project)];
}

/**
 * Why did this project land where it landed?
 *
 * Returns the full picture: the winner, whether it was pinned or fell through
 * to the type fallback, whether the top two zones tied, and the per-tier point
 * breakdown for all eight zones with the specific keywords that matched.
 *
 * Usage from the browser console on the map page:
 *
 *     const { explainProjectZone } = await import('/src/config/zones.js');
 *     console.table(explainProjectZone(someProject).scores);
 *
 * @param {object}  project
 * @param {object}  [parentProject]
 */
export function explainProjectZone(project, parentProject) {
  const winner = getProjectZone(project, parentProject);
  const pinned = resolvePinnedZone(project);
  const scored = pinned ? [] : scoreAllZones(project);
  const top = scored[0];
  const runnerUp = scored[1];

  return {
    projectId: project?.id ?? null,
    projectName: project?.name ?? null,
    winner: winner.id,
    winnerLabel: winner.label,
    pinned: pinned ? pinned.id : null,
    usedTypeFallback: !pinned && (!top || top.score === 0) && !parentProject,
    usedParentInheritance: !pinned && !!parentProject && (!top || top.score < INHERITED_ZONE_THRESHOLD),
    tiedAtTop: !!(top && runnerUp && top.score === runnerUp.score && top.score > 0),
    scores: scored.map(({ zone, score, breakdown }) => ({
      zone: zone.id,
      total: score,
      tag: breakdown.tagPts,
      dict: breakdown.dictPts,
      file: breakdown.filePts,
      category: breakdown.categoryPts,
      name: breakdown.namePts,
      desc: breakdown.descPts,
      matched: breakdown.matched.join(', '),
    })),
  };
}

/**
 * Generate positions for zones in a circular layout
 */
export function generateZonePositions(centerX = 0.5, centerY = 0.5, radius = 0.38) {
  const zoneIds = Object.keys(ZONES);
  const positions = {};

  zoneIds.forEach((id, index) => {
    const angle = (index / zoneIds.length) * Math.PI * 2 - Math.PI / 2;
    positions[id] = {
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius
    };
  });

  positions.uncategorized = { x: centerX, y: centerY + radius + 0.1 };

  return positions;
}

export const ZONE_POSITIONS = generateZonePositions();